import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useFormik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import InlineAlert from "src/grading/components/InlineAlert/InlineAlert";
import Loader from "src/grading/components/Loader/Loader";
import LoadingButton from "src/grading/components/LoadingButton/LoadingButton";
import EntryFrame from "src/grading/layout/EntryFrame";
import { refusalCode } from "src/grading/utils/apiError";
import { useAppDispatch } from "src/store/hooks";
import { useLoginMutation } from "src/store/reducers/auth/authApiSlice";
import { setCredentials } from "src/store/reducers/auth/authSlice";
import {
  useAcceptInviteMutation,
  useGetInviteQuery,
} from "src/store/reducers/invitations/invitationsApiSlice";
import * as Yup from "yup";

const PASSWORD_RULE =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])\S{8,99}$/;

const roleLabel: Record<string, string> = {
  platform_admin: "platform administrator",
  district_admin: "district administrator",
  school_admin: "school administrator",
  teacher: "teacher",
};

/**
 * Staff invitation acceptance. Districts without single sign-on create a password here;
 * invitees who already have an account sign in and the roles are applied.
 */
export const AcceptInvite = () => {
  const { t } = useTranslation();
  const eyebrow = t("auth.invitation", { defaultValue: "Invitation" });
  const { search } = useLocation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const token = new URLSearchParams(search).get("token") ?? "";
  const {
    data: invite,
    isLoading,
    isError,
    error: lookupError,
  } = useGetInviteQuery(token, { skip: !token });
  const [login, { isLoading: isLoggingIn }] = useLoginMutation();
  const [acceptInvite, { isLoading: isAccepting }] = useAcceptInviteMutation();
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState(false);
  /** 410 from the lookup or from accepting: the invitation was revoked or has expired. */
  const [goneCode, setGoneCode] = useState<
    "INVITATION_REVOKED" | "INVITATION_EXPIRED" | null
  >(null);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().when([], {
        is: () => !invite?.hasAccount,
        then: (s) =>
          s.required(
            t("invite.firstNameRequired", {
              defaultValue: "First name is required",
            }),
          ),
      }),
      lastName: Yup.string().when([], {
        is: () => !invite?.hasAccount,
        then: (s) =>
          s.required(
            t("invite.lastNameRequired", {
              defaultValue: "Last name is required",
            }),
          ),
      }),
      password: Yup.string()
        .required(
          t("invite.passwordRequired", {
            defaultValue: "Password is required",
          }),
        )
        .when([], {
          is: () => !invite?.hasAccount,
          then: (s) =>
            s.matches(
              PASSWORD_RULE,
              t("invite.passwordRule", {
                defaultValue:
                  "At least 8 characters with upper, lower, number and symbol",
              }),
            ),
        }),
      confirmPassword: Yup.string().when([], {
        is: () => !invite?.hasAccount,
        then: (s) =>
          s
            .oneOf(
              [Yup.ref("password")],
              t("invite.passwordsMatch", {
                defaultValue: "Passwords must match",
              }),
            )
            .required(
              t("invite.confirmRequired", {
                defaultValue: "Confirm your password",
              }),
            ),
      }),
    }),
    onSubmit: async (values) => {
      if (!invite) return;
      setError("");
      try {
        if (invite.hasAccount) {
          const tokens = await login({
            email: invite.email,
            password: values.password,
          }).unwrap();
          dispatch(setCredentials(tokens));
          await acceptInvite({ token }).unwrap();
        } else {
          await acceptInvite({
            token,
            registerUser: {
              firstName: values.firstName,
              lastName: values.lastName,
              email: invite.email,
              password: values.password,
            },
          }).unwrap();
        }
        setAccepted(true);
      } catch (err: unknown) {
        const code = refusalCode(err);
        if (code === "INVITATION_REVOKED" || code === "INVITATION_EXPIRED") {
          setGoneCode(code);
          return;
        }
        const raw =
          typeof err === "object" && err !== null
            ? (err as { data?: { message?: string | string[] } }).data?.message
            : undefined;
        const message = Array.isArray(raw) ? raw.join(", ") : raw;
        setError(
          message ||
            t("invite.acceptError", {
              defaultValue: "Could not accept the invitation.",
            }),
        );
      }
    },
  });

  const lookupCode = refusalCode(lookupError);
  const gone =
    goneCode ??
    (lookupCode === "INVITATION_REVOKED" || lookupCode === "INVITATION_EXPIRED"
      ? lookupCode
      : null);
  if (gone) {
    const revoked = gone === "INVITATION_REVOKED";
    return (
      <EntryFrame
        eyebrow={eyebrow}
        title={revoked ? t("invite.revokedTitle") : t("invite.expiredTitle")}
      >
        <InlineAlert severity="warning">
          {revoked ? t("invite.revokedBody") : t("invite.expiredBody")}
        </InlineAlert>
      </EntryFrame>
    );
  }
  if (!token || isError) {
    return (
      <EntryFrame
        eyebrow={eyebrow}
        title={t("invite.notFoundTitle", {
          defaultValue: "Invitation not found",
        })}
      >
        <InlineAlert severity="error">
          {t("invite.notFoundBody", {
            defaultValue:
              "This link is invalid or has expired. Ask your administrator to send a new invitation.",
          })}
        </InlineAlert>
      </EntryFrame>
    );
  }
  if (isLoading || !invite) {
    return (
      <EntryFrame
        eyebrow={eyebrow}
        title={t("auth.loadingInvitation", {
          defaultValue: "Loading your invitation",
        })}
      >
        <Loader />
      </EntryFrame>
    );
  }
  const org =
    invite.metadata.organizationName ??
    t("invite.yourDistrict", { defaultValue: "your district" });
  if (accepted) {
    return (
      <EntryFrame
        eyebrow={eyebrow}
        title={t("invite.acceptedTitle", { defaultValue: "You're in" })}
      >
        <Stack spacing={2.5}>
          <InlineAlert severity="success">
            {t("invite.accountReady", {
              defaultValue: "Your account for {{org}} is ready.",
              org,
            })}
          </InlineAlert>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => history.push("/grading")}
          >
            {t("invite.goToSignIn", { defaultValue: "Go to sign-in" })}
          </Button>
        </Stack>
      </EntryFrame>
    );
  }

  const { values, errors, touched, handleChange, handleSubmit } = formik;
  const roleId = invite.metadata.role ?? "";
  const role = t(`invite.role.${roleId || "staff"}`, {
    defaultValue: roleLabel[roleId] ?? "staff member",
  });

  return (
    <EntryFrame
      eyebrow={eyebrow}
      title={
        invite.metadata.inviterName
          ? t("invite.invitedBy", {
              defaultValue: "{{name}} invited you",
              name: invite.metadata.inviterName,
            })
          : t("invite.youreInvited", { defaultValue: "You're invited" })
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("invite.joinAs", {
          defaultValue: "Join {{org}} as a {{role}} using",
          org,
          role,
        })}{" "}
        <strong>{invite.email}</strong>.
      </Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2.5}>
          {!invite.hasAccount && (
            <>
              <TextField
                name="firstName"
                label={t("classes.firstName")}
                value={values.firstName}
                onChange={handleChange}
                error={touched.firstName && !!errors.firstName}
                helperText={touched.firstName && errors.firstName}
                autoComplete="given-name"
                fullWidth
              />
              <TextField
                name="lastName"
                label={t("classes.lastName")}
                value={values.lastName}
                onChange={handleChange}
                error={touched.lastName && !!errors.lastName}
                helperText={touched.lastName && errors.lastName}
                autoComplete="family-name"
                fullWidth
              />
            </>
          )}
          <TextField
            name="password"
            label={
              invite.hasAccount
                ? t("invite.yourPassword", { defaultValue: "Your password" })
                : t("invite.createPassword", {
                    defaultValue: "Create a password",
                  })
            }
            type="password"
            value={values.password}
            onChange={handleChange}
            error={touched.password && !!errors.password}
            helperText={touched.password && errors.password}
            autoComplete={
              invite.hasAccount ? "current-password" : "new-password"
            }
            fullWidth
          />
          {!invite.hasAccount && (
            <TextField
              name="confirmPassword"
              label={t("invite.confirmPassword", {
                defaultValue: "Confirm password",
              })}
              type="password"
              value={values.confirmPassword}
              onChange={handleChange}
              error={touched.confirmPassword && !!errors.confirmPassword}
              helperText={touched.confirmPassword && errors.confirmPassword}
              autoComplete="new-password"
              fullWidth
            />
          )}
          {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
          <LoadingButton
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            label={
              invite.hasAccount
                ? t("invite.signInAndAccept", {
                    defaultValue: "Sign in and accept",
                  })
                : t("invite.createAccount", { defaultValue: "Create account" })
            }
            loading={isLoggingIn || isAccepting}
          />
        </Stack>
      </Box>
    </EntryFrame>
  );
};
